// ProductsPage.js
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";

// Redux Actions
import { getProductsFunc } from "../../../store/custom-actions/products-actions/GetProductsSlicer";
import { deleteProductFunc } from "../../../store/custom-actions/products-actions/DeleteProductSlicer";
import { bulkDeleteFunc } from "../../../store/custom-actions/products-actions/BulkDeleteSlicer";
import { bulkUpdateProductsFunc } from "../../../store/custom-actions/products-actions/BulkUpdateProductsSlicer";
import { duplicateProductFunc } from "../../../store/custom-actions/products-actions/DuplicateProductSlicer";
import { exportProductsFunc, exportTemplateFunc } from "../../../store/custom-actions/products-actions/ExportProductsSlicer";

// Components
import PageTitle from "../../layouts/PageTitle";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import SearchBox from "../../components/ui/SearchBox";
import Table from "../../components/ui/Table";
import Pagination from "../../components/ui/Pagination";

// Helpers
import {
  handleDeleteEntity,
  handleBulkDeleteEntities,
} from "../../../utils/deleteHelper";
import checkPermissions from "../../global-functions/checkPermissionsFunc";
import hasPermission from "../../global-functions/hasPermission";

// Table Columns
import useProductTableColumns from "./productsTableColumns";

// Custom Forms
import DuplicateProductModal from "../../custom-forms/product-forms/DuplicateProductModal";
import BulkUpdateModal from "../../custom-forms/product-forms/BulkUpdateModal";
import ImportProductsModal from "../../custom-forms/product-forms/ImportProductsModal";

export default function ProductsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux Selectors
  const { loading: getProductsLoading, data: productsData } = useSelector(
    (state) => state.getAllProductsSlicer
  );
  const { loading: deleteProductLoading } = useSelector(
    (state) => state.deleteProductSlicer
  );
  const { loading: bulkDeleteProductsLoading } = useSelector(
    (state) => state.bulkDeleteSlicer
  );
  const { loading: duplicateProductLoading } = useSelector(
    (state) => state.duplicateProductSlicer
  );
  const { loading: bulkUpdateLoading } = useSelector(
    (state) => state.bulkUpdateProductsSlicer
  );
  const { loading: exportLoading, templateLoading } = useSelector(
    (state) => state.exportProductsSlicer
  );
  const { loading: importLoading } = useSelector(
    (state) => state.importProductsSlicer
  );

  // State
  const [productsList, setProductsList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filterName, setFilterName] = useState("All");
  const [recommendedStatusFilter, setRecommendedStatusFilter] = useState("");
  const [bestSellingFilter, setBestSellingFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [arrayWillDeleteIt, setArrayWillDeleteIt] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedProductForDuplicate, setSelectedProductForDuplicate] = useState(null);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const sort = 20;
  const activePag = useRef(0);
  const paginatedData = useRef([]);

  // Update data after fetch
  useEffect(() => {
    if (productsData) {
      setProductsList(productsData);
      updatePageData(productsData);
    }
  }, [productsData]);

  const updatePageData = (data) => {
    paginatedData.current = data.slice(
      activePag.current * sort,
      (activePag.current + 1) * sort
    );
  };

  const onPageChange = (pageIndex) => {
    activePag.current = pageIndex;
    updatePageData(productsList);
  };

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const lastQueryRef = useRef("");

  const filterFunc = (
    search = "",
    active = activeFilter,
    recomended = recommendedStatusFilter,
    bestSelling = bestSellingFilter
  ) => {
    const queryParts = [];
    if (recomended) queryParts.push(`recomended=${recomended}`);
    if (bestSelling) queryParts.push(`best_selling=${bestSelling}`);
    if (active) queryParts.push(`active=${active}`);
    if (search.trim()) queryParts.push(`search=${search.trim()}`);

    const query = "?" + queryParts.join("&");

    if (query == lastQueryRef.current) return;

    lastQueryRef.current = query;
    dispatch(getProductsFunc(query));
  };

  useEffect(() => {
    filterFunc();
    setIsFirstLoad(false);
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      filterFunc(searchValue);
    }
  }, [recommendedStatusFilter, bestSellingFilter, activeFilter]);

  // Delete Handlers
  const handleDeleteItem = (id) => {
    handleDeleteEntity({
      id,
      entityName: "Product",
      deleteFunc: deleteProductFunc,
      dispatch,
      refetchFunc: getProductsFunc,
      t,
    });
  };

  const handleBulkDeleteProducts = () => {
    handleBulkDeleteEntities({
      ids: arrayWillDeleteIt,
      entityName: "Products",
      deleteFunc: bulkDeleteFunc,
      dispatch,
      refetchFunc: getProductsFunc,
      t,
      onSuccess: () => setArrayWillDeleteIt([]),
    });
  };

  const handleCheckProduct = (id) =>
    setArrayWillDeleteIt((prev) => [...prev, id]);
  const handleUnCheckProduct = (id) =>
    setArrayWillDeleteIt((prev) => prev.filter((item) => item !== id));

  // Duplicate Product Handler
  const handleDuplicateProduct = (product) => {
    setSelectedProductForDuplicate(product);
    setShowDuplicateModal(true);
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setSelectedProductForDuplicate(null);
  };

  // Bulk Update Handler
  const handleOpenBulkUpdateModal = () => {
    if (arrayWillDeleteIt.length === 0) return;
    setShowBulkUpdateModal(true);
  };

  const handleBulkEditPricesQuantities = () => {
    if (arrayWillDeleteIt.length === 0) return;
    navigate(`/products/bulk-edit?ids=${arrayWillDeleteIt.join(",")}`);
  };

  const handleCloseBulkUpdateModal = () => {
    setShowBulkUpdateModal(false);
  };

  // Import/Export Handlers
  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
  };

  const handleExportProducts = async () => {
    try {
      const params = {};
      
      // If products are selected, export only those
      // Note: IDs are used for filtering only, not included in the exported Excel file
      // Products in Excel are identified by SKU only
      if (arrayWillDeleteIt.length > 0) {
        params.ids = arrayWillDeleteIt;
      }
      
      await dispatch(exportProductsFunc(params)).unwrap();
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await dispatch(exportTemplateFunc("en")).unwrap();
    } catch (error) {
      console.error("Template download error:", error);
    }
  };

  const handleImportSuccess = () => {
    // Refresh products list after successful import
    dispatch(getProductsFunc(""));
    setArrayWillDeleteIt([]);
  };

  const columns = useProductTableColumns({
    productsList,
    arrayWillDeleteIt,
    setArrayWillDeleteIt,
    handleCheckProduct,
    handleUnCheckProduct,
  });

  const renderActions = (product) => (
    <>
      {hasPermission("create products") && (
        <button
          className="btn btn-info shadow btn-xs sharp me-1"
          onClick={() => handleDuplicateProduct(product)}
          title={t("Duplicate Product")}
        >
          <i className="fas fa-copy"></i>
        </button>
      )}
      {hasPermission("update products") && (
        <Link
          to={`/products/update-item/${product.id}`}
          className="btn btn-primary shadow btn-xs sharp me-1"
        >
          <i className="fas fa-pencil-alt"></i>
        </Link>
      )}
      {hasPermission("delete products") && (
        <Link
          to="#"
          className="btn btn-danger shadow btn-xs sharp"
          onClick={() => handleDeleteItem(product.id)}
        >
          <i className="fa fa-trash"></i>
        </Link>
      )}
    </>
  );

  return (
    <>
      <PageTitle motherMenu="Dashboard" activeMenu="Products" />
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <SearchBox
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              onSearch={filterFunc}
            />

            <div className="d-flex align-items-center">
              {/* Filter Dropdown */}
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="primary">
                  {filterName}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setActiveFilter("");
                      setRecommendedStatusFilter("");
                      setBestSellingFilter("");
                      setFilterName("All");
                    }}
                  >
                    {t("All")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setActiveFilter(1);
                      setRecommendedStatusFilter("");
                      setBestSellingFilter("");
                      setFilterName("Active");
                    }}
                  >
                    {t("Active")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setRecommendedStatusFilter(1);
                      setActiveFilter("");
                      setBestSellingFilter("");
                      setFilterName("Recommended");
                    }}
                  >
                    {t("Recommended")}
                  </Dropdown.Item>
                  {/* <Dropdown.Item
                    onClick={() => {
                      setBestSellingFilter(1);
                      setActiveFilter("");
                      setRecommendedStatusFilter("");
                      setFilterName("Best Selling");
                    }}
                  >
                    {t("Best Selling")}
                  </Dropdown.Item> */}
                </Dropdown.Menu>
              </Dropdown>

              {/* Import/Export Dropdown */}
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="success">
                  <span className="d-none d-sm-inline">
                    {t("Import/Export")}
                  </span>
                  <i className="fas fa-file-excel d-inline d-sm-none"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleOpenImportModal}>
                    <i className="fas fa-file-import me-2"></i>
                    {t("Import Products")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleDownloadTemplate} disabled={templateLoading}>
                    {templateLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("Downloading...")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download me-2"></i>
                        {t("Download Template")}
                      </>
                    )}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleExportProducts} disabled={exportLoading}>
                    {exportLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("Exporting...")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-export me-2"></i>
                        {arrayWillDeleteIt.length > 0
                          ? t("Export Selected")
                          : t("Export All Products")}
                      </>
                    )}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* Bulk Actions Dropdown */}
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="danger">
                  <span className="d-none d-sm-inline">
                    {t("Bulk Actions")}
                  </span>
                  <i className="fa-solid fa-circle-exclamation d-inline d-sm-none"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    href="#"
                    className={arrayWillDeleteIt.length == 0 ? "disabled" : ""}
                    onClick={handleOpenBulkUpdateModal}
                  >
                    <i className="fas fa-edit me-2"></i>
                    {t("Update Products")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    className={arrayWillDeleteIt.length == 0 ? "disabled" : ""}
                    onClick={handleBulkEditPricesQuantities}
                  >
                    <i className="fas fa-tags me-2"></i>
                    {t("Update Prices & Quantities")}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    href="#"
                    className={arrayWillDeleteIt.length == 0 ? "disabled" : ""}
                    onClick={handleBulkDeleteProducts}
                  >
                    <i className="fas fa-trash me-2"></i>
                    {t("Delete Products")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* Create Button */}
              {checkPermissions(
                "create products",
                <Link to="/products/create" className="btn btn-primary">
                  <span className="d-none d-sm-inline">{t("Create")}</span>
                  <i className="fa-solid fa-plus d-inline d-sm-none"></i>
                </Link>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive w-100">
              <div className="dataTables_wrapper">
                <Table
                  columns={columns}
                  data={paginatedData.current}
                  loading={getProductsLoading}
                  actions={
                    hasPermission(["delete products", "update products", "create products"])
                      ? renderActions
                      : null
                  }
                />
                <Pagination
                  totalItems={productsList.length}
                  currentPage={activePag.current}
                  perPage={sort}
                  onPageChange={onPageChange}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadingScreen
        loading={deleteProductLoading || bulkDeleteProductsLoading || duplicateProductLoading || bulkUpdateLoading || exportLoading || importLoading}
      />
      
      <DuplicateProductModal
        show={showDuplicateModal}
        onHide={handleCloseDuplicateModal}
        product={selectedProductForDuplicate}
      />

      <BulkUpdateModal
        show={showBulkUpdateModal}
        onHide={handleCloseBulkUpdateModal}
        selectedIds={arrayWillDeleteIt}
        onSuccess={() => setArrayWillDeleteIt([])}
      />

      <ImportProductsModal
        show={showImportModal}
        onHide={handleCloseImportModal}
        onSuccess={handleImportSuccess}
      />
    </>
  );
}
